import React from 'react';
import { SectionHeader } from '../common';
import PlayerSearchInput from '../common/PlayerSearchInput';

const compactSearchClass = '[&_.form-input]:py-1.5 [&_.form-input]:text-sm [&_.form-input]:min-h-0';

function PlayerSlot({ name, tone, onRemove }) {
  const toneClass = {
    you: 'bg-surface border-border-medium text-text-primary',
    teammate: 'bg-success-light border-success text-success-dark',
    opponent: 'bg-danger-light border-danger text-danger-dark',
  }[tone];

  return (
    <div
      className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 min-w-0 shadow-sm ${toneClass}`}
    >
      <span className="text-xs sm:text-sm font-semibold truncate flex-1 text-left">{name}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="text-danger hover:text-danger-dark text-base font-bold leading-none shrink-0 px-0.5"
          title="Remove"
          aria-label={`Remove ${name}`}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

/**
 * Player picker + status + summary in one card (mobile-first).
 */
export function MatchupPlayerPicker({
  teammates = [],
  opponents = [],
  onAddTeammate,
  onRemoveTeammate,
  onAddOpponent,
  onRemoveOpponent,
  getAvailableTeammates,
  getAvailableOpponents,
  getPlayerDisplayName,
  isLoadingPlayers = false,
  hasSelections = false,
  resultsDescription = '',
  summary = null,
  isLoadingSummary = false,
}) {
  const showResultsBlock =
    hasSelections && (resultsDescription || summary || isLoadingSummary);

  return (
    <div className="mb-4 sm:mb-5">
      <div className="rounded-xl border border-border-medium bg-surface-subtle shadow-sm overflow-hidden">
        <div className="p-3 sm:p-4 md:p-5">
          <SectionHeader
            title="Compare players"
            subtitle="Add a teammate, opponent, or both to see your record"
            size="sm"
            className="mb-3"
          />
          <div className="flex flex-row items-stretch gap-2 sm:gap-4 min-w-0">
            <div className="flex-1 min-w-0 flex flex-col">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-info mb-1.5 truncate">
                Your team
              </p>
              <div className="space-y-1.5 flex-1">
                <PlayerSlot name="You" tone="you" />
                {teammates.length > 0 ? (
                  <PlayerSlot
                    name={getPlayerDisplayName(teammates[0])}
                    tone="teammate"
                    onRemove={onRemoveTeammate}
                  />
                ) : (
                  <PlayerSearchInput
                    placeholder="Teammate"
                    availablePlayers={getAvailableTeammates()}
                    selectedPlayer={null}
                    onSelectPlayer={onAddTeammate}
                    onRemovePlayer={() => {}}
                    disabled={isLoadingPlayers}
                    className={`w-full min-w-0 ${compactSearchClass}`}
                  />
                )}
              </div>
            </div>

            <div
              className="flex flex-col items-center justify-center shrink-0 self-center px-0.5 sm:px-2"
              aria-hidden
            >
              <div className="bg-danger text-text-inverse rounded-full w-9 h-9 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center shadow-md border-2 border-surface">
                <span className="text-xs sm:text-base md:text-lg font-black">VS</span>
              </div>
              <span className="mt-1 hidden sm:block text-[9px] text-text-muted font-bold tracking-widest uppercase">
                Matchups
              </span>
            </div>

            <div className="flex-1 min-w-0 flex flex-col">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-danger-dark mb-1.5 truncate">
                Opponents
              </p>
              <div className="space-y-1.5 flex-1">
                {opponents.length > 0 ? (
                  <PlayerSlot
                    name={getPlayerDisplayName(opponents[0])}
                    tone="opponent"
                    onRemove={() => onRemoveOpponent(opponents[0].id)}
                  />
                ) : (
                  <PlayerSearchInput
                    placeholder="Opponent"
                    availablePlayers={getAvailableOpponents()}
                    selectedPlayer={null}
                    onSelectPlayer={onAddOpponent}
                    onRemovePlayer={() => {}}
                    disabled={isLoadingPlayers}
                    className={`w-full min-w-0 ${compactSearchClass}`}
                  />
                )}
                {opponents.length === 2 ? (
                  <PlayerSlot
                    name={getPlayerDisplayName(opponents[1])}
                    tone="opponent"
                    onRemove={() => onRemoveOpponent(opponents[1].id)}
                  />
                ) : opponents.length === 1 ? (
                  <PlayerSearchInput
                    placeholder="2nd opp."
                    availablePlayers={getAvailableOpponents()}
                    selectedPlayer={null}
                    onSelectPlayer={onAddOpponent}
                    onRemovePlayer={() => {}}
                    disabled={isLoadingPlayers}
                    className={`w-full min-w-0 ${compactSearchClass}`}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {showResultsBlock ? (
          <div className="border-t border-border-medium bg-surface/60 px-3 py-3 sm:px-4 sm:py-4 space-y-3">
            {resultsDescription ? (
              <p className="text-sm text-info font-medium leading-snug">{resultsDescription}</p>
            ) : null}
            {isLoadingSummary ? (
              <div className="flex justify-center py-5" aria-busy="true">
                <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary border-t-transparent" />
              </div>
            ) : null}
            {summary && !isLoadingSummary ? summary : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MatchupPlayerPicker;
